import { Spinner } from "../spinner";
import { useFormContext } from "./form-context";
import { Button, type ButtonProps } from "./ui/button";

export type SubmitButtonProps = Omit<ButtonProps, "type"> & {
  isPending?: boolean;
  /**
   * When the button is pressable.
   * - "always" (default) — pressable whenever the form is idle. An invalid form
   *   still cannot submit (`handleSubmit` refuses); pressing runs validation and
   *   reveals every error.
   * - "when-changed" — additionally requires `isDirty`, so an untouched edit
   *   form cannot re-submit unchanged values.
   *
   * The button is NEVER disabled for being invalid: a disabled button explains
   * nothing, and under the display policy a user who has not blurred anything
   * would see neither a usable button nor a single error message.
   */
  enable?: "always" | "when-changed";
};

export function SubmitButton({
  isPending,
  disabled,
  enable = "always",
  children,
  ...props
}: SubmitButtonProps) {
  const form = useFormContext();

  return (
    <form.Subscribe
      selector={(state: any) => ({
        isSubmitting: state.isSubmitting,
        isDirty: state.isDirty,
        isValid: state.isValid,
      })}
    >
      {({
        isSubmitting,
        isDirty,
        isValid,
      }: {
        isSubmitting: boolean;
        isDirty: boolean;
        isValid: boolean;
      }) => (
        <Button
          type="submit"
          disabled={
            isPending ||
            isSubmitting ||
            disabled ||
            (enable === "when-changed" && !isDirty) ||
            !isValid
          }
          kind="solid"
          variant="primary"
          {...props}
        >
          {(isPending || isSubmitting) && <Spinner />}
          {children}
        </Button>
      )}
    </form.Subscribe>
  );
}
